// src/pages/Dashboards/SingleDashboard.tsx

import {
  DashboardCustomizeTwoTone,
  DeleteTwoTone,
  EditTwoTone,
  ExpandMoreTwoTone,
  SettingsTwoTone,
} from '@mui/icons-material'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Chip,
  Collapse,
  SpeedDial,
  SpeedDialAction,
  Stack,
  Typography,
} from '@mui/material'
import { SyntheticEvent, useEffect, useState, useMemo } from 'react'

import { PanelId } from '@/types/domain'
import {
  DashboardId,
  DashboardName,
  DashboardPool,
} from '@/types/domain/dashboard'

import {
  userLocales,
  DDMMYYYY,
  getStorage,
  setStorage,
  notifyError,
} from '../../api'
import {
  AddFirstPanel,
  DashboardGranularity,
  DlgDelete,
  DlgDashboardEdit,
  DlgPanelAdd,
  SinglePanel,
  TogglePanels,
  HStack,
} from '../../components'
import { ListManager } from '../../components/dnd/ListManager'
import {
  useSensorPool,
  useConsumptionApi,
  useDashboardApi,
  useDashboardPool,
  useDatapointApi,
  usePanelApi,
} from '../../hooks'

const germanDate = date =>
  new Date(date).toLocaleDateString(userLocales, DDMMYYYY)

const sleep = ms => new Promise(r => setTimeout(r, ms))

type SingleDashboardProps = {
  expanded: boolean
  id: DashboardId
  onChange: (_event: SyntheticEvent, _isExpanded: boolean) => void
  PAGE_CONFIG?: string
}

export const SingleDashboard = ({
  expanded,
  id,
  onChange,
  PAGE_CONFIG = 'settings_dashboard_id',
}: SingleDashboardProps) => {
  // hooks

  const dPool: DashboardPool = useDashboardPool()

  const { getDashboardConsumption, invalidate: invalidateDashboard } =
    useConsumptionApi()

  const { getDatapointsByPanelIds } = useDatapointApi()
  const sPool = useSensorPool()

  const { remove } = useDashboardApi(null)

  const {
    getPanels,
    move: movePanel,
    updatePanels,
    invalidate: invalidatePanels,
  } = usePanelApi()

  // states

  const [panels, setPanels] = useState([])
  const [refreshTick, setRefreshTick] = useState(0)

  const [options, setOptions] = useState<{
    start_date?: string
    end_date?: string
    granularity?: string
  }>({})

  const [isFactorError, setIsFactorError] = useState(false)
  const [factorErrorSensor, setFactorErrorSensor] = useState(null)

  const [config, setConfig] = useState(getStorage(PAGE_CONFIG))
  useEffect(() => setStorage(PAGE_CONFIG, config), [config, PAGE_CONFIG])

  const [showDlg, setShowDlg] = useState<
    false | 'DlgAddTile' | 'DlgEditDashboard' | 'DlgDeleteDashboard'
  >(false)

  const [panelsAndConsumptions, setPanelsAndConsumptions] = useState([])
  const [dashboardDatapoints, setDashboardDatapoints] = useState<any>({})

  const [maxColItems, setMaxColItems] = useState(config?.maxColItems ?? 2)
  useEffect(() => setConfig(prev => ({ ...prev, maxColItems })), [maxColItems])

  const [dashboardData, setDashboardData] = useState({
    name: null,
    startDate: null,
    endDate: null,
    granularity: 'lastFullMonth',
  })

  const [nextPosition, setNextPosition] = useState(0)
  const [lastPanelDefaults, setLastPanelDefaults] = useState(null)

  const dashboardGranularity = config?.granularity ?? 'lastFullMonth'

  const dateRangeLabel = `${germanDate(dashboardData.startDate)} - ${germanDate(dashboardData.endDate)}`

  const actions = [
    {
      fn: () => setShowDlg('DlgAddTile'),
      icon: <DashboardCustomizeTwoTone />,
      name: 'Neue Kachel',
    },
    {
      fn: () => setShowDlg('DlgEditDashboard'),
      icon: <EditTwoTone />,
      name: `${dashboardData.name} bearbeiten`,
    },
    {
      fn: () => setShowDlg('DlgDeleteDashboard'),
      icon: <DeleteTwoTone />,
      name: `${dashboardData.name} löschen`,
    },
  ]

  // fn's

  const endFullYear = new Date().getFullYear() - 1

  const earliestPanelStartYear = useMemo(() => {
    const sensorIds = Object.values(dashboardDatapoints.items || {})
      .flat()
      .map(Number)

    const years = sPool
      .filter(sensor => sensorIds.includes(Number(sensor.attributes.entity_id)))
      .map(sensor => sensor.attributes.data_sampling_date)
      .filter(Boolean)
      .map(date => new Date(date).getFullYear())
      .filter(Number.isFinite)

    return years.length > 0 ? Math.min(...years) : new Date().getFullYear()
  }, [dashboardDatapoints, sPool])

  const safeEarliestYear = Math.min(
    earliestPanelStartYear ?? endFullYear,
    endFullYear
  )

  const onChangeGranularity = event => {
    const mode = event.target.value

    const y: number = new Date().getFullYear()
    const m: number = new Date().getMonth()
    const d: number = new Date().getDate()

    let fromDate, toDate, granularity

    switch (mode) {
      case 'lastFullMonth':
        fromDate = new Date(y, m - 1, 1)
        toDate = new Date(y, m, 0)
        granularity = 'd'
        break
      case 'lastThirtyDays':
        fromDate = new Date(y, m, d - 30)
        toDate = new Date(y, m, d)
        granularity = 'd'
        break
      case 'lastTwoMonths':
        fromDate = new Date(y, m - 2, 1)
        toDate = new Date(y, m, 0)
        granularity = 'm'
        break
      case 'lastSixMonths':
        fromDate = new Date(y, m - 6, 1)
        toDate = new Date(y, m, 0)
        granularity = 'm'
        break
      case 'thisYear':
        fromDate = new Date(y, 0, 1)
        toDate = new Date(y, m, d)
        granularity = 'm'
        break
      case 'lastYear':
        fromDate = new Date(y - 1, 0, 1)
        toDate = new Date(y - 1, 12, 0)
        granularity = 'm'
        break
      case 'lastTwoYears':
        fromDate = new Date(y - 2, 0, 1)
        toDate = new Date(y - 1, 11, 31)
        granularity = 'y'
        break
      case 'allYears':
        fromDate = new Date(safeEarliestYear, 0, 1)
        toDate = new Date()
        granularity = 'y'
        break
      default:
        fromDate = new Date(y, m - 1, 1)
        toDate = new Date(y, m, 0)
        granularity = 'd'
    }

    setConfig(prev => ({ ...prev, granularity: mode }))

    // en-CA (!) as user locales; formats to YYYY-MM-DD
    const start_date = fromDate.toLocaleDateString('en-CA', DDMMYYYY)
    const end_date = toDate.toLocaleDateString('en-CA', DDMMYYYY)
    const options = { start_date, end_date, granularity }

    ;(async () => {
      await updatePanels(id, options)
      invalidateDashboard(id)
      setOptions(options)
    })()
  }

  const onClose = () => setShowDlg(false)

  const onDragEnd = (startIndex, endIndex) => {
    // dragging operation did not changed panel position
    if (startIndex === endIndex) return

    const list = Array.from(panelsAndConsumptions)
    const [removed] = list.splice(startIndex, 1)
    list.splice(endIndex, 0, removed)

    const dId: DashboardId = Number(list[endIndex].attributes.dashboard_id)
    const pId: PanelId = Number(list[endIndex].id)

    movePanel(dId, pId, {
      old_position: Number(startIndex),
      new_position: Number(endIndex),
    })

    setPanelsAndConsumptions(list)
  }

  const onSavePanel = async () => {
    invalidatePanels(id)

    const prevIds = new Set(panels.map(p => String(p.id)))

    const pollPanels = async (retries = 5, delay = 200) => {
      const list = await getPanels(id, true)
      const hasNew =
        list.length > prevIds.size || list.some(p => !prevIds.has(String(p.id)))
      if (hasNew || retries <= 1) return list
      await sleep(delay)
      return pollPanels(retries - 1, delay)
    }

    const list = await pollPanels()

    const pollDatapoints = async (retries = 4, delay = 250) => {
      const pool = await getDatapointsByPanelIds(id, list)
      const anyEmpty = Object.values(pool.items || {}).some(
        arr => Array.isArray(arr) && arr.length === 0
      )
      if (anyEmpty && retries > 0) {
        await sleep(delay)
        return pollDatapoints(retries - 1, delay)
      }
      return pool
    }
    await pollDatapoints().catch(() => null)

    setPanels(list ? list : [])
    setPanelsAndConsumptions(list ? list : [])

    invalidateDashboard(id)
    setRefreshTick(t => t + 1)

    setShowDlg(false)
  }

  const onDeleted = async deletedId => {
    invalidatePanels(id)

    const pollPanels = async (retries = 5, delay = 200) => {
      const list = await getPanels(id, true)
      const stillThere = list.some(p => String(p.id) === String(deletedId))
      if (!stillThere || retries <= 1) return list
      await sleep(delay)
      return pollPanels(retries - 1, delay)
    }

    const list = await pollPanels()
    setPanels(list ? list : [])
    setPanelsAndConsumptions(list ? list : [])
  }

  // useEffects

  useEffect(() => {
    const fetchDatapoints = async () => {
      if (!id || panels.length === 0) return

      const data = await getDatapointsByPanelIds(id, panels)
      setDashboardDatapoints(data)
    }

    fetchDatapoints()
  }, [id, panels, getDatapointsByPanelIds])

  // get panels in dashboard(id)
  useEffect(() => {
    const fetchPanels = async () => {
      const data = await getPanels(id)
      setPanels(data)
    }

    if (id) fetchPanels()
  }, [id, options, getPanels])

  // get consumption in dashboard(id)
  useEffect(() => {
    const fetchConsumption = async () => {
      try {
        await getDashboardConsumption(id)
      } catch (error) {
        setIsFactorError(true)
        setFactorErrorSensor(error?.message)
        notifyError(`Error fetching dashboard consumption: ${error?.message}`)
      }
    }

    fetchConsumption()
  }, [id, options, getDashboardConsumption])

  // get dashboard name
  useEffect(() => {
    if (dPool.length === 0) return

    const setDashboardName = () => {
      const dRecord = dPool.find(f => Number(f.id) === id)
      const name: DashboardName = dRecord?.attributes?.name ?? 'Neues Dashboard'

      setDashboardData(prev => ({ ...prev, name }))
    }

    setDashboardName()
  }, [id, dPool])

  useEffect(() => {
    if (panels.length === 0) return

    const positions = panels
      .map(p => Number(p?.attributes?.position ?? -1))
      .filter(n => Number.isFinite(n) && n >= 0)

    const nextPos = positions?.length ? Math.max(...positions) + 1 : 0
    const lastPanel = panels.at(-1)

    const { indicator, graphic_type } = lastPanel.attributes

    setPanelsAndConsumptions(panels)
    setLastPanelDefaults({ indicator, graphic_type })
    setNextPosition(nextPos)
  }, [panels])

  useEffect(() => {
    const y: number = new Date().getFullYear()
    const m: number = new Date().getMonth()
    const d: number = new Date().getDate()

    let startDate
    let endDate
    let granularity

    switch (dashboardGranularity) {
      case 'lastFullMonth':
        startDate = new Date(y, m - 1, 1)
        endDate = new Date(y, m, 0)
        granularity = 'd'
        break

      case 'lastThirtyDays':
        startDate = new Date(y, m, d - 30)
        endDate = new Date(y, m, d)
        granularity = 'd'
        break

      case 'lastTwoMonths':
        startDate = new Date(y, m - 2, 1)
        endDate = new Date(y, m, 0)
        granularity = 'm'
        break

      case 'lastSixMonths':
        startDate = new Date(y, m - 6, 1)
        endDate = new Date(y, m, 0)
        granularity = 'm'
        break

      case 'thisYear':
        startDate = new Date(y, 0, 1)
        endDate = new Date(y, m, d)
        granularity = 'm'
        break

      case 'lastYear':
        startDate = new Date(y - 1, 0, 1)
        endDate = new Date(y - 1, 11, 31)
        granularity = 'm'
        break

      case 'lastTwoYears':
        startDate = new Date(y - 2, 0, 1)
        endDate = new Date(y - 1, 11, 31)
        granularity = 'y'
        break

      case 'allYears': {
        const endYear = y
        const startYear = Math.min(earliestPanelStartYear ?? 2018, endYear)

        startDate = new Date(startYear, 0, 1)
        endDate = new Date()
        granularity = 'y'
        break
      }

      default:
        startDate = new Date(y, m - 1, 1)
        endDate = new Date(y, m, 0)
        granularity = 'd'
    }

    startDate = startDate.toLocaleDateString('en-CA', DDMMYYYY)
    endDate = endDate.toLocaleDateString('en-CA', DDMMYYYY)

    setDashboardData(prev => ({ ...prev, startDate, endDate, granularity }))
  }, [dashboardGranularity, earliestPanelStartYear])

  return (
    <Accordion
      expanded={expanded}
      onChange={onChange}
      sx={{ '&.Mui-expanded': { m: 0 }, p: 1 }}
    >
      <AccordionSummary expandIcon={<ExpandMoreTwoTone />}>
        <Typography variant={'h6'}>{dashboardData.name}</Typography>

        <Chip label={dateRangeLabel} sx={{ ml: 2 }} />
      </AccordionSummary>

      <Stack>
        {expanded === true && (
          <AccordionDetails>
            <HStack>
              {/* panels by row (1-5) */}
              <TogglePanels
                onChange={rows => setMaxColItems(rows)}
                value={maxColItems}
              />

              {/* dashboard granularity */}
              <DashboardGranularity
                onChange={event => onChangeGranularity(event)}
                value={dashboardGranularity}
                earliestYear={earliestPanelStartYear}
              />
            </HStack>

            {/* display warning in case of factor issues */}
            <Collapse in={isFactorError}>
              <Alert severity={'warning'}>
                Bitte prüfen Sie die Faktoren für Messpunkt {factorErrorSensor}.
              </Alert>
            </Collapse>

            {/* drag-n-drop wrapper for panels  */}
            <ListManager
              direction={'horizontal'}
              items={panelsAndConsumptions}
              maxItems={maxColItems}
              onDragEnd={onDragEnd}
              render={item => (
                <SinglePanel
                  dashboardId={id}
                  onDeleted={onDeleted}
                  id={Number(item.id)}
                  panelsPerRow={maxColItems}
                  refreshKey={`${options?.start_date || ''}|${options?.end_date || ''}|${options?.granularity || ''}|${refreshTick}`}
                />
              )}
            />

            {/* if dashboard is empty -> no panel added yet, then display a hint to create one */}
            {panelsAndConsumptions.length === 0 && (
              <AddFirstPanel onClick={() => setShowDlg('DlgAddTile')} />
            )}

            {/* display speed dial only if accordion is expanded */}
            {expanded && (
              <SpeedDial
                ariaLabel={'SpeedDial'}
                icon={<SettingsTwoTone />}
                sx={{ position: 'absolute', bottom: 16, right: 16 }}
              >
                {actions.map(action => (
                  <SpeedDialAction
                    icon={action.icon}
                    key={action.name}
                    onClick={action.fn}
                    tooltipTitle={action.name}
                  />
                ))}
              </SpeedDial>
            )}
          </AccordionDetails>
        )}
      </Stack>

      {/* add */}
      <DlgPanelAdd
        data={{ id, lastPanelDefaults, nextPosition }}
        onClose={onClose}
        onSave={onSavePanel}
        open={showDlg === 'DlgAddTile'}
      />

      {/* edit */}
      <DlgDashboardEdit
        data={{
          id,
          granularity: dashboardGranularity,
          name: dashboardData.name,
        }}
        onChangeGranularity={onChangeGranularity}
        onChangeName={name => setDashboardData(prev => ({ ...prev, name }))}
        onClose={onClose}
        open={showDlg === 'DlgEditDashboard'}
      />

      {/* delete */}
      <DlgDelete
        onCancel={onClose}
        onConfirm={() => remove(id)}
        open={showDlg === 'DlgDeleteDashboard'}
      >
        <Typography sx={{ fontSize: 16 }}>
          Möchten Sie Dashboard <strong>{dashboardData.name}</strong> wirklich
          löschen?
        </Typography>

        <Typography sx={{ fontSize: 16 }}>
          Der Vorgang kann nicht rückgängig gemacht werden.
        </Typography>
      </DlgDelete>
    </Accordion>
  )
}
